import { ID } from "@nova/constants"
import { InfraredFlight, InfraredImage } from "@nova/protobuf"
import { createSelector } from "@reduxjs/toolkit"
import {
    selectInfraredFlightById,
    selectHotspotPointLayerIsVisible,
    selectHotspotLabelLayerIsVisible,
    selectRemovedHotspotsIsVisible,
    selectHotspotPolygonIsVisible,
    selectHotspotOrthoIsVisible,
    selectFlightPathIsEnabled,
    selectHotspotScanAreaIsEnabled,
} from "features/entity-caching/flights/selectors"
import {
    selectHotspotsByFlightId,
    selectFilteredHotspotsByFlightId,
    selectSensitivityFilteredHotspotsByFlightId,
    selectSensitivityAndDistanceFilteredHotspotsByFlightId,
} from "features/entity-caching/hotspots/selectors"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { selectShowLayerByIdMapForHotspotOrthoOverlaps } from "features/map/layers/selectors"
import { RootState } from "integrations/redux"
import { memoize } from "nodash"

//pass a selector to this to build a new selector that takes an array of ids and then runs the selector on all of the ids
export function createAggregateSelector<Id, Item>(
    selectorFactory: (id: Id) => (state: RootState) => Item[] | Item
) {
    return memoize(
        (ids: Id[]) => {
            const perIdSelectors = ids.map((id) => selectorFactory(id))
            return createSelector(perIdSelectors, (...results: (Item[] | Item)[]) =>
                results.flatMap((result) => (Array.isArray(result) ? result : [result]))
            )
        },
        (ids) => ids.join(",")
    )
}

//a aggregate variant that checks for some truthy value in the result array
export function createAggregateTruthySelector<Id, Result>(
    selectorFactory: (id: Id) => (state: RootState) => Result
) {
    return memoize(
        (ids: Id[]) => {
            const perIdSelectors = ids.map((id) => selectorFactory(id))
            return createSelector(perIdSelectors, (...results: Result[]) => results.some(Boolean))
        },
        (ids) => ids.join(",")
    )
}

export const selectAllMergedFlights = createAggregateSelector(selectInfraredFlightById)
export const selectAllMergedHotspots = createAggregateSelector(selectHotspotsByFlightId)
export const selectAllMergedFilteredHotspots = createAggregateSelector(
    selectFilteredHotspotsByFlightId
)
export const selectAllMergedFilteredHotspotsBySensitivity = createAggregateSelector(
    selectSensitivityFilteredHotspotsByFlightId
)
export const selectAllMergedFilteredHotspotsBySensitivityAndDistance = createAggregateSelector(
    selectSensitivityAndDistanceFilteredHotspotsByFlightId
)

export const selectAllMergedFlightUploads = createAggregateSelector(
    selectInfraredImagesAndUploadsByFlightId
)

export const selectMergedFlightPathIsShowing =
    createAggregateTruthySelector(selectFlightPathIsEnabled)

export const selectMergeScanAreaIsShowing = createAggregateTruthySelector(
    selectHotspotScanAreaIsEnabled
)
export const selectMergePointLayerIsVisible = createAggregateTruthySelector(
    selectHotspotPointLayerIsVisible
)

export const selectSomeFlightHasScanAreaExtraFeature = memoize((flightId: string) =>
    createSelector([selectMergedFlightById(flightId)], (mergedFlight) =>
        mergedFlight.hotspotFlights.some((flight) =>
            flight.job?.extraFeatures?.includes("scanArea")
        )
    )
)

export const selectLargestIndividualFlightInMergedFlight = memoize((flightId: string) =>
    createSelector([selectMergedFlightById(flightId)], (mergedFlight) => {
        return mergedFlight.hotspotFlights.reduce(
            (largestFlight, curFlight) => {
                if ((curFlight.job?.area || 0) > (largestFlight?.job?.area || 0)) {
                    return curFlight
                }
                return largestFlight
            },
            undefined as InfraredFlight | undefined
        )
    })
)

export const selectLargestIndividualFlightInMergedFlightWithScanArea = memoize((flightId: string) =>
    createSelector([selectMergedFlightById(flightId)], (mergedFlight) => {
        return mergedFlight.hotspotFlights.reduce(
            (largestFlight, curFlight) => {
                if (!curFlight.job?.extraFeatures?.includes("scanArea")) return largestFlight
                if ((curFlight.job?.area || 0) > (largestFlight?.job?.area || 0)) {
                    return curFlight
                }
                return largestFlight
            },
            undefined as InfraredFlight | undefined
        )
    })
)

export const selectMergedOrthoOverlapIsShowing = memoize((ids: ID[]) =>
    createSelector([selectShowLayerByIdMapForHotspotOrthoOverlaps], (overlaps) => {
        return ids.some((id) => overlaps[id])
    })
)

export const selectMergeLabelsAreVisible = createAggregateTruthySelector(
    selectHotspotLabelLayerIsVisible
)

export const selectMergeRemovedHotspotsAreVisible = createAggregateTruthySelector(
    selectRemovedHotspotsIsVisible
)

export const selectMergePolygonsAreVisible = createAggregateTruthySelector(
    selectHotspotPolygonIsVisible
)

export const selectMergeOrthosAreVisible = createAggregateTruthySelector(
    selectHotspotOrthoIsVisible
)

export const selectMergedInfraredFlightImages = memoize((ids: ID[]) =>
    createSelector([selectAllMergedFlights(ids)], (flights) => {
        const images: InfraredImage[][] = []
        flights.forEach((flight) => {
            if (flight?.images) images.push(flight.images)
        })
        return images.flat()
    })
)
